/*
Copyright 2022-2024 New Vector Ltd.
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/** Day a week starts on; "auto" follows the runtime's locale data. */
/** Order the day, month and year are typed in. */
export type DateOrder = "dmy" | "mdy" | "ymd";

export type FirstDayOfWeek =
  | "auto"
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

export interface ConfigOptions {
  /**
   * The Posthog endpoint to which analytics data will be sent.
   * This is only used in the full package of Element Call.
   */
  posthog?: {
    api_key: string;
    api_host: string;
  };

  /**
   * The Sentry endpoint to which crash data will be sent.
   * This is only used in the full package of Element Call.
   */
  sentry?: {
    DSN: string;
    environment: string;
  };

  /**
   * The rageshake server to which feedback and debug logs will be sent.
   * This is only used in the full package of Element Call.
   */
  rageshake?: {
    submit_url: string;
  };

  /**
   * Sets the URL to send opentelemetry data to. If unset, opentelemetry will
   * be disabled. This is only used in the full package of Element Call.
   */
  opentelemetry?: {
    collector_url: string;
  };

  // Describes the default homeserver to use. The same format as Element Web
  // (without identity servers as we don't use them).
  default_server_config?: {
    ["m.homeserver"]: {
      base_url: string;
      server_name: string;
    };
  };

  // Describes the LiveKit configuration to be used.
  livekit?: {
    // The link to the service that returns a livekit url and token to use it.
    // This is a fallback link in case the homeserver in use does not advertise
    // a livekit service url in the client well-known.
    // The well known needs to be formatted like so:
    // {"type":"livekit", "livekit_service_url":"https://livekit.example.com"}
    // and stored under the key: "org.matrix.msc4143.rtc_foci"
    livekit_service_url: string;
  };

  /**
   * TEMPORARY experimental features.
   */
  features?: {
    /**
     * Allow to join group calls without audio and video.
     */
    feature_group_calls_without_video_and_audio?: boolean;

    /**
     * Send device-specific call session membership state events instead of
     * legacy user-specific call membership state events.
     * This setting has no effect when the user joins an active call with
     * legacy state events. For compatibility, Element Call will always join
     * active legacy calls with legacy state events.
     */
    feature_use_device_session_member_events?: boolean;
  };

  /**
   * A link to the software and services license agreement (SSLA)
   */
  ssla?: string;

  media_devices?: {
    /**
     * Defines whether participants should start with audio enabled by default.
     */
    enable_audio?: boolean;

    /**
     * Defines whether participants should start with video enabled by default.
     */
    enable_video?: boolean;
  };

  /**
   * Whether upon entering a room, the user should be prompted to launch the
   * native mobile app. (Affects only Android and iOS.)
   *
   * Note that this can additionally be disabled by the app's URL parameters.
   */
  app_prompt?: boolean;

  /**
   * URL of the registration proxy service for guest account creation.
   * When set, guest registration bypasses UIA and uses the server-side
   * proxy instead, which signs requests with a shared secret that never
   * leaves the server.
   */
  guest_registration_url?: string;

  /**
   * Base URL of the scheduling admin API. When set together with
   * schedulers_room_id, authorized users can schedule meetings from the
   * home page. The browser authorizes with the user's own access token;
   * no service credential is ever shipped to the client.
   */
  admin_api_url?: string;

  /**
   * Matrix room id whose joined members are allowed to schedule meetings.
   * The scheduling form is only shown to users who are joined to this room.
   */
  schedulers_room_id?: string;

  /**
   * Defaults for the scheduling form and the calendar views. The block and
   * each field in it are optional; anything left out falls back to
   * CALENDAR_DEFAULTS.
   */
  calendar?: {
    /**
     * IANA time zone recorded with a meeting when the browser does not report
     * one of its own.
     */
    default_timezone?: string;
    /** Meeting lengths offered, in minutes. */
    duration_options?: number[];
    /** Meeting length preselected in the scheduling form, in minutes. */
    default_duration_minutes?: number;
    /** Reminder lead times offered, in minutes. */
    reminder_options?: number[];
    /** Reminder lead time preselected in the scheduling form, in minutes. */
    default_reminder_minutes?: number;
    /** Day the week starts on in the calendar grids. */
    first_day_of_week?: FirstDayOfWeek;
    /**
     * Hours the time grid highlights as working time and scrolls to on
     * opening. The grid still renders the whole day, so a meeting outside
     * them is never hidden.
     */
    day_start_hour?: number;
    day_end_hour?: number;
    /**
     * Order the day, month and year are typed in, and the character written
     * between them. Native date inputs follow the browser's own locale and
     * ignore the page's, so a mixed fleet of workstations would otherwise ask
     * staff to type a different order on each machine.
     */
    date_input_order?: DateOrder;
    date_input_separator?: string;
    /**
     * Show times on a 24-hour clock. Entry is 24-hour either way, so turning
     * this off makes the app display a clock it does not accept.
     */
    time_display_24h?: boolean;
  };

  /**
   * Branding and identity configuration. All fields are optional so the
   * source ships with neutral defaults that fall back to upstream Element
   * Call's branding. Deployments override these values at runtime via
   * config.json without modifying source.
   */
  branding?: {
    /** Product name shown in titles, aria-labels, and log lines. */
    product_name?: string;
    /** URL of a logo asset to render on branded screens. */
    logo_url?: string;
    /** URL of the square logo mark shown in the in-call footer. */
    logo_mark_url?: string;
    /** URL of the wordmark shown next to the mark in the in-call footer. */
    logo_type_url?: string;
    /** Link target for the home-page CTA. */
    website_url?: string;
    /** URL to a privacy policy. The consent line is hidden when unset. */
    privacy_policy_url?: string;
    /** Anchor text for the license footer (e.g. operator name). */
    footer_text?: string;
    /** Anchor href for the license footer. */
    footer_url?: string;
    /** Public source code URL (AGPL §13 source offer). */
    source_code_url?: string;
    /** Matrix state event type for scheduled meeting metadata. */
    meeting_event_type?: string;
    /** Localpart prefix for the admin-api bot user. */
    bot_user_prefix?: string;
    /** Minutes before scheduled start to allow joining. Defaults to 5. */
    early_join_minutes?: number;
  };

  /**
   * These are low level options that are used to configure the MatrixRTC session.
   * Take care when changing these options.
   */
  matrix_rtc_session?: {
    /**
     * How long (in milliseconds) to wait before rotating end-to-end media encryption keys
     * when someone leaves a call.
     */
    wait_for_key_rotation_ms?: number;

    /**
     * The duration (in milliseconds) after the most recent keep-alive (delayed leave event restart)
     * that the server waits before sending the leave MatrixRTC membership event.
     */
    delayed_leave_event_delay_ms?: number;

    /**
     * The time (in milliseconds) after which we consider a delayed event restart http request to have failed.
     * Setting this to a lower value will result in more frequent retries but also a higher chance of failiour.
     *
     * In the presence of network packet loss (hurting TCP connections), the custom delayedEventRestartLocalTimeoutMs
     * helps by keeping more delayed event reset candidates in flight,
     * improving the chances of a successful reset. (its is equivalent to the js-sdk `localTimeout` configuration,
     * but only applies to calls to the `_unstable_updateDelayedEvent` endpoint with a body of `{action:"restart"}`.)
     */
    delayed_leave_event_restart_local_timeout_ms?: number;

    /**
     * The time interval (in milliseconds) at which the client sends membership keep-alive
     * messages to the server by restarting the timer for the delayed leave event.
     */
    delayed_leave_event_restart_ms?: number;

    /**
     * How long we wait before retrying after a network error on any of the requests.
     */
    network_error_retry_ms?: number;

    /**
     * The timeout (in milliseconds) after we joined the call, that our membership should expire
     * unless we have explicitly updated it.
     *
     * This is what goes into the m.rtc.member event expiry field and is typically set to a number of hours.
     */
    membership_event_expiry_ms?: number;
  };
}

/**
 * Fallbacks for the optional `calendar` block, applied at each read site.
 * They deliberately do not live in DEFAULT_CONFIG: that is deep-merged with
 * the deployment's config, which would splice a shorter option array into the
 * default one rather than replacing it.
 */
export const CALENDAR_DEFAULTS = {
  default_timezone: "UTC",
  date_input_order: "dmy" as DateOrder,
  date_input_separator: ".",
  time_display_24h: true,
  duration_options: [15, 30, 45, 60],
  default_duration_minutes: 30,
  reminder_options: [15, 30, 60, 120],
  default_reminder_minutes: 30,
  first_day_of_week: "auto",
  day_start_hour: 8,
  day_end_hour: 18,
} satisfies NonNullable<ConfigOptions["calendar"]>;

// Overrides members from ConfigOptions that are always provided by the
// default config and are therefore non-optional.
export interface ResolvedConfigOptions extends ConfigOptions {
  default_server_config: {
    ["m.homeserver"]: {
      base_url: string;
      server_name: string;
    };
  };
  ssla: string;
  app_prompt: boolean;
}

export const DEFAULT_CONFIG: ResolvedConfigOptions = {
  default_server_config: {
    ["m.homeserver"]: {
      base_url: "http://localhost:8008",
      server_name: "localhost",
    },
  },
  features: {
    feature_use_device_session_member_events: true,
  },
  ssla: "https://static.element.io/legal/element-software-and-services-license-agreement-uk-1.pdf",
  app_prompt: true,
};
